import { ReactNode } from 'react';
import {
    AddShoppingCart,
    AdminPanelSettings,
    AlternateEmail,
    AssignmentAdd,
    AssuredWorkloadOutlined,
    ReportProblem,
    AttachMoney,
    AutoGraph,
    CastForEducation,
    Construction,
    CrisisAlert,
    CurrencyExchange,
    Discount,
    Email,
    DocumentScanner,
    Draw,
    FolderCopy,
    Grade,
    Groups,
    Handyman,
    History,
    ImportContacts,
    Inventory,
    Key,
    Lan,
    LocationCity,
    MarkEmailRead,
    Message,
    NewReleases,
    PersonAddAlt1,
    Place,
    PointOfSale,
    Queue,
    Quiz,
    Receipt,
    Forum,
    RingVolume,
    Payments,
    Schema,
    Score,
    Settings,
    ShoppingCartCheckout,
    Speed,
    Subscriptions,
    SupervisorAccount,
    Web,
    WebAsset,
    Widgets,
} from '@mui/icons-material';
import { permissionManager, Permissions } from '../permissions/manager';

/**
 * Returns true when the entry should be shown for the current permissions.
 *
 * Takes the whole permissions payload rather than a roles string, because since 2026-09-10 that
 * is what `usePermissions()` hands back and an entry may be gated on an ACL resource rather than
 * a role.
 */
export type VisibilityCheck = (permissions: Permissions) => boolean;

/** Anything in the menu that can be hidden by role — groups and entries alike. */
export interface Gated {
    visible?: VisibilityCheck;
}

/** A hand-written link to a route that is not a plain resource list. */
export interface MenuLinkEntry extends Gated {
    kind: 'link';
    to: string;
    label: string;
    icon: ReactNode;
}

/** A registered react-admin <Resource>, rendered by <Menu.ResourceItem>. */
export interface MenuResourceEntry extends Gated {
    kind: 'resource';
    name: string;
}

export type MenuEntry = MenuLinkEntry | MenuResourceEntry;

/** A labelled run of entries inside a section. */
export interface MenuGroup extends Gated {
    heading: string;
    entries: MenuEntry[];
}

/** A collapsible top-level section of the sidebar. */
export interface MenuSection extends Gated {
    kind: 'section';
    title: string;
    icon: ReactNode;
    groups: MenuGroup[];
}

/** Anything that can sit at the top level: a collapsible section, or a bare item. */
export type MenuNode = MenuSection | MenuEntry;

/**
 * Whether the admin can see any of these sections, which is what a menu entry offers.
 *
 * Always `view`: a sidebar link leads to a list, and being able to edit a coupon without being
 * able to open the coupons screen is not a shape the tree can express.
 *
 * **It takes several sections because most groups link to more than one.** Gating a group on a
 * single resource was defensible while a grant covered a whole screen, and stopped being so with
 * per-verb permissions: the People & Company group links to contacts as well as companies, and
 * those are two sections at two different tiers, so a role holding contacts alone saw no group at
 * all and lost links to screens that were theirs. Same for Operators, Workflows and the three Mini
 * tools. Listing what a group actually links to is what keeps the menu honest.
 */
const canView = (permissions: Permissions, ...sections: string[]): boolean =>
    sections.some((section: string): boolean =>
        permissionManager.isAllowed(permissions, section + '.view'));

const link: (to: string, label: string, icon: ReactNode, visible?: VisibilityCheck) => MenuLinkEntry =
    (to: string, label: string, icon: ReactNode, visible?: VisibilityCheck): MenuLinkEntry =>
        ({ kind: 'link', to, label, icon, visible });

const resource: (name: string, visible?: VisibilityCheck) => MenuResourceEntry =
    (name: string, visible?: VisibilityCheck): MenuResourceEntry =>
        ({ kind: 'resource', name, visible });

const section: (title: string, icon: ReactNode, groups: MenuGroup[]) => MenuSection =
    (title: string, icon: ReactNode, groups: MenuGroup[]): MenuSection =>
        ({ kind: 'section', title, icon, groups });

/** The route an entry points at. Resources live at /<name>. */
export const entryPath: (entry: MenuEntry) => string =
    (entry: MenuEntry): string =>
        entry.kind === 'resource' ? `/${entry.name}` : entry.to;

/** The text an entry is filtered on. Resource labels are translated at render time. */
export const entryLabel: (entry: MenuEntry) => string =
    (entry: MenuEntry): string =>
        entry.kind === 'resource' ? entry.name.replace(/_/g, ' ') : entry.label;

/** True when the entry's route is the current one, or a child of it (edit/create). */
export const entryMatchesPath: (entry: MenuEntry, pathname: string) => boolean =
    (entry: MenuEntry, pathname: string): boolean => {
        const path: string = entryPath(entry);
        return pathname === path || pathname.startsWith(`${path}/`);
    };

/** Where a section's open/closed state is persisted. */
export const sectionStoreKey: (title: string) => string =
    (title: string): string => `hq.menu.${title}`;

/**
 * The whole sidebar, as data. Adding an item is one line here — AdminMenu renders
 * this and owns none of the structure itself.
 */
export const menuTree: MenuNode[] = [
    section('Customer', <ImportContacts />, [
        {
            heading: 'Customer Accounts',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Customer::accounts'),
            entries: [link('/customer_accounts', 'Accounts', <SupervisorAccount />)],
        },
        {
            heading: 'Customers',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Customer::customers'),
            entries: [resource('customers')],
        },
    ]),
    section('Sales', <PointOfSale />, [
        {
            heading: 'Enterprise Requests',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Sales::enterprise_requests'),
            entries: [link('/enterprise_requests', 'Enterprise Requests', <CrisisAlert />)],
        },
        {
            heading: 'Contact Form',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Sales::contact_forms'),
            entries: [link('/contact_forms', 'Contact Forms', <RingVolume />)],
        },
        {
            heading: 'Conversations',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Sales::conversation'),
            entries: [link('/sales_conversations', 'Conversations', <Forum />)],
        },
        {
            heading: 'Stripe',
            visible: (permissions): boolean =>
                canView(
                    permissions,
                    'HQ_Sales::coupons',
                    'HQ_Sales::subscriptions',
                    'HQ_Sales::invoices',
                    'HQ_Sales::products',
                    'HQ_Sales::product_prices',
                    'HQ_Sales::refunds',
                ),
            entries: [
                link('/coupons', 'Coupons', <Discount />),
                link('/subscriptions', 'Subscriptions', <Subscriptions />),
                link('/subscription_invoices', 'Invoices', <Receipt />),
                link('/subscription_products', 'Products', <Inventory />),
                link('/subscription_product_prices', 'Product Prices', <AttachMoney />),
                link('/subscription_refunds', 'Refunds', <CurrencyExchange />),
            ],
        },
    ]),
    link('/cms_blocks', 'CMS Blocks', <Web />,
        (permissions): boolean =>
            canView(permissions, 'HQ_Content::cms_blocks')),
    section('Data', <FolderCopy />, [
        {
            heading: 'EmailFinder',
            // Had no gate at all, so every admin saw these links while HQ-API refused
            // everyone below super admin. The menu now says what the API will allow.
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::email_finder', 'HQ_Data::email_finder_results'),
            entries: [
                link('/email_finders', 'EmailFinder', <AlternateEmail />),
                link('/email_finder_results', 'EmailFinder Results', <MarkEmailRead />),
            ],
        },
        {
            heading: 'Mystery Shopper',
            // Had no gate at all, so every admin saw these links while HQ-API refused
            // everyone below super admin. The menu now says what the API will allow.
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::mystery_shopper', 'HQ_Data::mystery_shopper_results'),
            entries: [
                link('/mystery_shoppers', 'MysteryShopper', <ShoppingCartCheckout />),
                link('/mystery_shopper_results', 'MysteryShopper Results', <AddShoppingCart />),
            ],
        },
        {
            heading: 'Compliance Sniffer',
            // Had no gate at all, so every admin saw these links while HQ-API refused
            // everyone below super admin. The menu now says what the API will allow.
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::compliance_sniffer', 'HQ_Data::compliance_sniffer_results'),
            entries: [
                link('/compliance_sniffers', 'ComplianceSniffers', <AssuredWorkloadOutlined />),
                link('/compliance_sniffer_results', 'ComplianceSniffer Results', <Speed />),
            ],
        },
        {
            heading: 'Nok Processing',
            // Had no gate at all, so every admin saw these links while HQ-API refused
            // everyone below super admin. The menu now says what the API will allow.
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::nok_processing_failures'),
            entries: [
                link('/nok_processing_failures', 'Scoring Failures', <ReportProblem />),
            ],
        },
        {
            heading: 'Email',
            // Either channel's view grant is enough to open the list; HQ-API then returns only
            // the channels the admin holds (`EmailMessageChannelExtension`).
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Nok::noks', 'HQ_Sales::conversation'),
            entries: [
                link('/email_messages', 'Email Messages', <Email />),
            ],
        },
        {
            heading: 'People & Company',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::companies', 'HQ_Data::crm'),
            entries: [
                resource('companies'),
                resource('addresses'),
                resource('emails'),
                resource('employees'),
                resource('contacts'),
                resource('social_media'),
                resource('telephones'),
                resource('urls'),
                resource('websites'),
            ],
        },
        {
            heading: 'Companies House',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Data::companies_house'),
            entries: [
                link('/companies_houses', 'Companies House', <LocationCity />),
                link('/companies_house_addresses', 'C.H Addresses', <Place />),
                link('/companies_house_peoples', 'C.H People', <Groups />),
                link('/filing_histories', 'C.H Filings', <DocumentScanner />),
            ],
        },
    ]),
    link('/customer_tutorials', 'Customer Tutorials', <CastForEducation />,
        (permissions): boolean =>
            canView(permissions, 'HQ_Customer::tutorials')),
    section('System', <AdminPanelSettings />, [
        {
            heading: 'Nok Minis',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Nok::minis', 'HQ_Nok::mini_grades', 'HQ_Nok::mini_results'),
            entries: [
                link('/nok_minis', 'Nok Minis', <Widgets />),
                link('/mini_grades', 'Mini Grades', <Grade />,
                    (permissions): boolean =>
                        canView(permissions, 'HQ_Nok::mini_grades')),
                link('/mini_results', 'Mini Grade Results', <Score />,
                    (permissions): boolean =>
                        canView(permissions, 'HQ_Nok::mini_results')),
            ],
        },
        {
            heading: 'Noks',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Nok::noks'),
            entries: [link('/noks', 'Noks', <Quiz />)],
        },
        {
            heading: 'Faux Nokkers',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_Nok::faux_nokkers'),
            entries: [
                link('/nok_websites', 'Nok Websites', <WebAsset />),
                link('/nok_personas', 'Nok Personas', <PersonAddAlt1 />),
                link('/nok_email_templates', 'Nok Email Templates', <AssignmentAdd />),
                link('/nok_email_signatures', 'Nok Email Signatures', <Draw />),
            ],
        },
        {
            heading: 'Operators',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_System::operators', 'HQ_System::tools', 'HQ_System::token_histories'),
            entries: [
                link('/operators', 'Operators', <Lan />),
                link('/tools', 'Tools', <Handyman />),
                link('/token_histories', 'Token History', <History />),
            ],
        },
        {
            heading: 'Workflows',
            visible: (permissions): boolean =>
                canView(permissions, 'HQ_System::workflows', 'HQ_System::prompts', 'HQ_System::model_schemas'),
            entries: [
                link('/workflows', 'Workflows', <AutoGraph />),
                link('/prompts', 'Prompts', <Message />),
                link('/model_schemas', 'Workflow Schemas', <Schema />),
            ],
        },
        {
            heading: 'Settings & Admin Users',
            entries: [
                link(
                    '/settings', 'Settings', <Settings />,
                    (permissions): boolean =>
                        canView(permissions, 'HQ_Admin::settings'),
                ),
                link(
                    '/llm_model_prices', 'LLM Prices', <Payments />,
                    (permissions): boolean =>
                        canView(permissions, 'HQ_Admin::llm_prices'),
                ),
                // Gated here as well as by react-admin's own canAccess. A <Menu.ResourceItem>
                // hides itself at render time, which is too late for `visibleGroups`: it has
                // already counted the entry, kept the group and drawn the section heading. That
                // left an empty System section for a role with no admin access at all.
                resource('admin_users', (permissions): boolean =>
                    canView(permissions, 'HQ_Admin::admin_users')),
                // Only for admins who may change a window. Everyone else learns of one from the
                // banner, and a screen they can only look at is not worth a place in the sidebar.
                // No `canView`: the resource has no verbs, so it is checked as it is.
                link(
                    '/maintenance', 'Maintenance', <Construction />,
                    (permissions): boolean =>
                        permissionManager.isAllowed(permissions, 'HQ_System::maintenance'),
                ),
                // HQ-API's queues, schedules and backlogs. No `canView`: the resource has no verbs,
                // so it is checked as it is. The gate matches the one on `GET /hq/queues`, so the
                // entry appears for exactly the admins HQ-API would let in.
                link(
                    '/system-queues', 'Queues', <Queue />,
                    (permissions): boolean =>
                        permissionManager.isAllowed(permissions, 'HQ_System::queues'),
                ),
                // Gated on the ACL resource that guards the screen itself, so the entry appears
                // for exactly the admins HQ-API would let in.
                link(
                    '/admin_roles',
                    'Admin Roles',
                    <Key />,
                    (permissions): boolean =>
                        canView(permissions, 'HQ_Admin::roles'),
                ),
            ],
        },
    ]),
    // No gate: every admin who uses HQ is told what has shipped. HQ-API asks only for a session.
    link('/whats-new', "What's new", <NewReleases />),
];
